import { moduleApi } from "@amtana/module";
import type { EraseStep } from "@amtana/module";

import type {
  DeliverInput,
  DeliverOutput,
  PlannedDelivery,
  ReleaseHoldInput,
  RouterFactInput,
  SendErasureReminderInput,
} from "./notification-router.schemas.ts";
import type {
  MarkAllNoticesReadOutput,
  MarkNoticeReadInput,
  Notice,
  NotificationPreference,
  NotificationSettings,
  NoticesForUserInput,
  SetEmailDigestInput,
  SetNotificationChannelInput,
  SetNotificationEnabledInput,
  SetProductUpdatesInput,
  SetQuietHoursInput,
  SetSnoozeInput,
  SetTipsInput,
  UnreadNoticeCount,
  UnsubscribeConfirmation,
  UnsubscribeInput,
  UnsubscribeOutcome,
} from "./notification.schemas.ts";
import type {
  AddSlackChannelInput,
  RemoveSlackChannelInput,
  SlackChannel,
  TestPostSlackChannelInput,
  TestPostSlackChannelOutput,
  UpdateSlackChannelInput,
} from "./slack-channel.schemas.ts";

// The promise notification makes to everyone else. A module states a fact (ADR-022); the
// router asks `planDeliveries` who hears it and `deliverOne` to send a single delivery,
// respecting the recipient's own settings. `notify` as a peer door is gone (point 10, S26.15):
// this module only owns delivery and preference, not the events themselves.

/** Whoever the operation is for; every wire procedure fills it from the caller's session. */
export type ForUser = { readonly userId: string };

/** Who made a staff write; ops fills it from the caller's session before the audit (ADR-005
 * point 5). */
export type ForActor = { readonly actorUserId: string };

export interface NotificationApi extends EraseStep {
  settingsForUser(input: ForUser): Promise<NotificationSettings>;
  setEnabled(input: ForUser & SetNotificationEnabledInput): Promise<NotificationSettings>;
  setChannel(input: ForUser & SetNotificationChannelInput): Promise<NotificationSettings>;
  noticesForUser(input: ForUser & NoticesForUserInput): Promise<Notice[]>;
  unreadNoticeCount(input: ForUser): Promise<UnreadNoticeCount>;
  markNoticeRead(input: ForUser & MarkNoticeReadInput): Promise<Notice>;
  markAllNoticesRead(input: ForUser): Promise<MarkAllNoticesReadOutput>;
  /** This person's do-not-disturb and digest preference (S7.05/S7.07); defaults when they never set one. */
  preferenceForUser(input: ForUser): Promise<NotificationPreference>;
  /** Pauses their email for 1-7 days, starting now; in-app notices keep landing. */
  setSnooze(input: ForUser & SetSnoozeInput): Promise<NotificationPreference>;
  /** Ends an active snooze early. */
  clearSnooze(input: ForUser): Promise<NotificationPreference>;
  /** A daily window, in their own time zone, their email waits out. */
  setQuietHours(input: ForUser & SetQuietHoursInput): Promise<NotificationPreference>;
  /** Immediate, daily or weekly: how often their email rounds up instead of firing per event. */
  setEmailDigest(input: ForUser & SetEmailDigestInput): Promise<NotificationPreference>;
  /** Spends a mailed unsubscribe link (S7.03, RFC 8058): no caller, a signed token names who and
   * what. Reached by the one-click POST (REST) and the confirmation screen's button (tRPC), never
   * by a GET; answers ok even for a token already spent, since turning email off twice is harmless. */
  unsubscribeByToken(input: UnsubscribeInput): Promise<UnsubscribeOutcome>;
  /** Where a GET of the one-click URL sends a person: the confirmation screen. Changes nothing. */
  unsubscribeConfirmationUrl(input: UnsubscribeInput): Promise<UnsubscribeConfirmation>;
  /** Turns product-update email on or off (S6.09); on by default. */
  setProductUpdates(input: ForUser & SetProductUpdatesInput): Promise<NotificationPreference>;
  /** Turns tips email on or off (S6.09); on by default. */
  setTips(input: ForUser & SetTipsInput): Promise<NotificationPreference>;

  // Slack channels (ADR-022 point 6, S26.07): staff-only, configured in /ops through these
  // peer-only doors, never on the wire. Notification owns the channel (talking to Slack) and
  // its configuration; ops owns the staff audit and the screen.

  /** Every Slack channel, never the webhook URL (S26.07: "never shown back"). */
  listSlackChannels(): Promise<readonly SlackChannel[]>;
  /** Adds a channel: a name, its webhook URL (sealed at rest) and the facts it hears. */
  addSlackChannel(input: ForActor & AddSlackChannelInput): Promise<SlackChannel>;
  /** Changes a channel; a field left out keeps its stored value, a new webhook URL replaces the
   * sealed one (never read back to edit in place). */
  updateSlackChannel(input: ForActor & UpdateSlackChannelInput): Promise<SlackChannel>;
  /** Removes a channel outright; no further post is ever queued for it. */
  removeSlackChannel(input: ForActor & RemoveSlackChannelInput): Promise<void>;
  /** Posts a fixed test message to the channel's webhook, proving it before it is relied on. */
  testPostSlackChannel(input: ForActor & TestPostSlackChannelInput): Promise<TestPostSlackChannelOutput>;

  // The router (ADR-022 points 3 and 5, S26.05/S26.06): the Go worker decides which facts to
  // react to; these two doors are where notification decides who hears one and sends a single
  // delivery. Peer-only, reached through `modules/worker-bridge`'s signed callback, never on the
  // wire.

  /** Who hears `fact`, and on which channel; an empty list is not a refusal, only nobody to tell
   * (the rule table, `ROUTER_RULES`, covers every production fact as of S26.08/S26.11-S26.14). */
  planDeliveries(fact: RouterFactInput): Promise<readonly PlannedDelivery[]>;
  /** Sends one delivery, unless its key already went out; idempotent, so a retried call after a
   * successful send never reaches the provider twice. A `transactionalEmail` kind that needs a
   * link mints it itself, after that same "already delivered" check (ADR-022 revision 5), so a
   * retried or replayed, already-delivered intent never mints again (D526's own guarantee, now
   * implicit in this one check rather than a door worker-bridge asked first). */
  deliverOne(input: DeliverInput): Promise<DeliverOutput>;

  // Holding (ADR-022 point 9 revision 4, S26.16): snooze, quiet hours, bursts and digests
  // release as a wake of `notification-hold`'s process manager (services/worker/internal/
  // pipelines/notification_hold.go), a pure timer; this door alone decides what is due.

  /** A wake fired for this person: sends whatever is now due (an ended snooze or quiet hours, a
   * folded burst, a due digest), and re-schedules the next wake itself when something remains. */
  releaseHold(input: ReleaseHoldInput): Promise<void>;

  // The day-before erasure reminder (ADR-026 point 9, epic 30, S30.13): the `erasure` process
  // manager's own wake, the exact shape `releaseHold` above already takes; peer-only, reached
  // only through `modules/worker-bridge`'s signed callback.

  /** Mails the reminder, in the person's own language, straight off what the wake carries — no
   * fact of its own, no account lookup. */
  sendErasureReminder(input: SendErasureReminderInput): Promise<void>;
}

export const NotificationApi = moduleApi<NotificationApi>()("notification");
